import React, { useContext, useEffect, useRef, useState } from 'react';
import logo from '../../assets/images/logo-small.png';
import Text from '../../components/Text';
import { useAuthContext } from '../../contexts/AuthContext';
import { ResponsiveContext } from '../../contexts/ResponsiveContext';
import {
  ArrowBackLine,
  Avatar,
  AvatarWhite,
  Burger,
  ChatBlack,
  ChevronDownFadedBlack,
  DashboardBlack,
  Lamp,
  LongArrowLeft,
  MatLogo,
} from '../../icons';
import { EntityKind } from '../../models/User';
import { UserHttp } from '../../services/http/UserHttp';
import { useMenuStore } from '../../store/menuStore';
import { useUserRequestStore } from '../../store/UserRequestStore';
import {
  AppIconButton,
  AppIconWrapper,
  DropdownArrow,
  DropdownArrowContainer,
  DropdownDivider,
  ElementContainer,
  ElementContainerItems,
  ElementContainerItemsNavigation,
  HeaderContainer,
  HeaderContainerItems,
  HeaderDropdownContainer,
  HeaderFixed,
  HeaderLeftGroup,
  HeaderLinkDropdownStyle,
  HeaderLinkStyle,
  HeaderTitle,
  HelpIconContainer,
  Logo,
  LogoContainer,
  LogoLink,
  LogoLinkMobile,
  LogoMobileContainer,
  MenuButton,
  MenuButtonIcon,
  MenuButtonText,
  NotificationBadge,
  SpaceArea,
  SpaceButton,
  SpaceContainer,
  UserAvatarFallback,
  UserAvatarImage,
  UserTextContainer,
  UserTextLine,
} from './HeaderStyle';

const {
  REACT_APP_URL_API_LOGOUT,
  REACT_APP_URL_EXT_PREVENTION_DEMANDES,
  REACT_APP_URL_EXT_PREVENTION_MON_PROFIL,
  REACT_APP_URL_EXT_PREVENTION_SERVICE,
  REACT_APP_URL_EXT_PREVENTION_ESP,
  REACT_APP_URL_PREVENTIONBTP_PREFIXE,
} = process.env;

const entityKindToNameMap: Record<EntityKind, string> = {
  [EntityKind.UNKNOWN]: '',
  [EntityKind.ORGANIZATION]: 'entreprise',
  [EntityKind.BRANCH]: 'filiale',
  [EntityKind.CORPORATION]: 'groupe',
};

// Helper function to construct URLs with parameters
const constructUrl = (template: string, params: Record<string, string | number>): string => {
  let url = template;
  for (const [key, value] of Object.entries(params)) {
    url = url.replace(`{${key}}`, String(value));
  }
  return url;
};

interface HeaderElementProps {
  text: string;
  icon: any;
  mobileText?: string;
  link?: string;
  size?: string;
  tabletOnly?: boolean;
  notificationCount?: number;
}

export default function Header(): JSX.Element {
  const { authenticated, user } = useAuthContext();
  const responsive = useContext(ResponsiveContext);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [helpOpened, setHelpOpened] = useState(false);
  const switchOpened = useMenuStore((state) => state.switchOpen);
  const organizationId = user?.groupId;

  const [requests, setRequests] = useUserRequestStore((state) => [state.requests, state.setRequests]);

  const triggerZendeskLauncher = () => {
    if (window.zE) {
      if (!helpOpened) {
        window.zE('messenger', 'show');
        window.zE('messenger', 'open');
        setHelpOpened(true);
      } else {
        window.zE('messenger', 'close');
        setHelpOpened(false);
      }
    } else {
      console.warn('Zendesk (window.zE) non chargé.');
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch user requests when authenticated
  useEffect(() => {
    if (!authenticated) return;

    UserHttp.getRequests()
      .then((fetchedRequests) => {
        if (fetchedRequests && fetchedRequests.length > 0) {
          setRequests(fetchedRequests);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch user requests:', error);
      });
  }, [authenticated, setRequests]);

  return (
    <header className="header">
      <HeaderFixed>
        <HeaderContainer>
          <HeaderContainerItems>
            <div className={'header-container-items'}>
              <LogoContainer>
                <LogoLink href={REACT_APP_URL_PREVENTIONBTP_PREFIXE}>
                  <Logo src={logo} alt={'logo'} />
                  {!responsive.isMobile && <Text fsize={'xs'}>Un service OPPBTP</Text>}
                </LogoLink>
                <LogoMobileContainer>
                  <LogoLinkMobile href={REACT_APP_URL_API_LOGOUT}>
                    <ArrowBackLine width={'21px'} height={'24px'} />
                  </LogoLinkMobile>
                </LogoMobileContainer>
              </LogoContainer>
              <HeaderLeftGroup>
                <MenuButton onClick={switchOpened} role="button">
                  <MenuButtonIcon>
                    <Burger />
                  </MenuButtonIcon>
                  <MenuButtonText>Menu</MenuButtonText>
                </MenuButton>
                <AppIconButton
                  href={
                    REACT_APP_URL_EXT_PREVENTION_ESP && user
                      ? constructUrl(REACT_APP_URL_EXT_PREVENTION_ESP, {
                          entityType: entityKindToNameMap[EntityKind.ORGANIZATION],
                          organizationId: organizationId ?? -1,
                        })
                      : REACT_APP_URL_EXT_PREVENTION_SERVICE || '#'
                  }
                >
                  <AppIconWrapper>
                    <MatLogo className="icon-mat" />
                    <LongArrowLeft className="icon-long-arrow-left" />
                  </AppIconWrapper>
                </AppIconButton>
                <HeaderTitle>Suivi du matériel</HeaderTitle>
              </HeaderLeftGroup>
              <ElementContainer>
                <ElementContainerItems>
                  <ElementContainerItemsNavigation>
                    <HeaderLink
                      text={'Mon espace'}
                      mobileText={'Mon espace'}
                      icon={DashboardBlack}
                      link={
                        REACT_APP_URL_EXT_PREVENTION_ESP && user
                          ? constructUrl(REACT_APP_URL_EXT_PREVENTION_ESP, {
                              entityType: entityKindToNameMap[EntityKind.ORGANIZATION],
                              organizationId: organizationId ?? -1,
                            })
                          : REACT_APP_URL_EXT_PREVENTION_SERVICE || '#'
                      }
                      size={'22px'}
                      tabletOnly={true}
                    />

                    <SpaceButton
                      className="HelpButton"
                      onClick={triggerZendeskLauncher}
                      role="button"
                      tabIndex={0}
                    >
                      <HelpIconContainer>
                        <Lamp />
                      </HelpIconContainer>
                      <span>Aide</span>
                    </SpaceButton>
                    <SpaceContainer ref={dropdownRef}>
                      <SpaceButton
                        className="SpaceButton"
                        onClick={(e) => {
                          e.preventDefault();
                          setDropdownOpen((prev) => !prev);
                        }}
                      >
                        <NotificationBadge count={requests?.length ?? 0}>
                          {requests?.length ?? 0}
                        </NotificationBadge>
                        {user?.pictureRemoteUrl ? (
                          <UserAvatarImage src={user?.pictureRemoteUrl} alt="profil utilisateur" />
                        ) : (
                          <UserAvatarFallback>
                            <AvatarWhite />
                          </UserAvatarFallback>
                        )}
                        <UserTextContainer>
                          <UserTextLine isBold>Bonjour {user?.firstname ?? ''}</UserTextLine>
                          <UserTextLine isSmall>{user?.email ?? ''}</UserTextLine>
                        </UserTextContainer>
                        <ChevronDownFadedBlack />
                      </SpaceButton>
                      {isDropdownOpen && (
                        <SpaceArea className="SpaceArea">
                          <div className={'header-dropdown'}>
                            <div className="dropdown-pointer-container">
                              <div className="dropdown-pointer" />
                            </div>
                            <DropdownArrowContainer>
                              <DropdownArrow />
                            </DropdownArrowContainer>
                            <HeaderDropdownContainer>
                              <HeaderLinkDropdown
                                text={'Mon compte personnel'}
                                icon={Avatar}
                                link={
                                  REACT_APP_URL_EXT_PREVENTION_MON_PROFIL && user
                                    ? constructUrl(REACT_APP_URL_EXT_PREVENTION_MON_PROFIL, {
                                        entityType: entityKindToNameMap[EntityKind.ORGANIZATION],
                                        organizationId: organizationId ?? -1,
                                      })
                                    : '#'
                                }
                              />
                              <HeaderLinkDropdown
                                text={'Mes demandes'}
                                icon={ChatBlack}
                                link={
                                  REACT_APP_URL_EXT_PREVENTION_DEMANDES && user
                                    ? constructUrl(REACT_APP_URL_EXT_PREVENTION_DEMANDES, {
                                        entityType: entityKindToNameMap[EntityKind.ORGANIZATION],
                                        organizationId: organizationId ?? -1,
                                      })
                                    : '#'
                                }
                                notificationCount={requests?.length}
                              />
                              <DropdownDivider />
                              <HeaderLinkDropdown
                                text={authenticated ? 'Se déconnecter' : 'Se connecter'}
                                icon={ArrowBackLine}
                                link={authenticated ? REACT_APP_URL_API_LOGOUT : ''}
                              />
                            </HeaderDropdownContainer>
                          </div>
                        </SpaceArea>
                      )}
                    </SpaceContainer>
                  </ElementContainerItemsNavigation>
                </ElementContainerItems>
              </ElementContainer>
            </div>
          </HeaderContainerItems>
        </HeaderContainer>
      </HeaderFixed>
    </header>
  );
}

function HeaderLinkDropdown(props: HeaderElementProps): JSX.Element {
  const Icon = props.icon || React.Fragment;
  return (
    <li>
      <HeaderLinkDropdownStyle href={props.link}>
        <Icon width={'20px'} height={'20px'} />
        <span>{props.text}</span>
        {props.notificationCount !== undefined && (
          <NotificationBadge count={props.notificationCount}>{props.notificationCount}</NotificationBadge>
        )}
      </HeaderLinkDropdownStyle>
    </li>
  );
}

function HeaderLink(props: HeaderElementProps): JSX.Element {
  const Icon = props.icon || React.Fragment;
  return (
    <HeaderLinkStyle href={props.link} tabletOnly={props.tabletOnly}>
      <Icon width={props.size} height={props.size} />
      <span className={'desktop-text'}>{props.text}</span>
      <span className={'mobile-tablet-text'}>{props.mobileText}</span>
    </HeaderLinkStyle>
  );
}
